import copy from 'copy-to-clipboard';
import { FC, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import {
	Box,
	DotsHorizontalOutline,
	EyeOffOutline,
	FlagOutline,
	IconButton,
	LinkAltOutline,
	MailOpenOutline,
	PencilAltOutline,
	Popover,
	PopoverContent,
	PopoverTrigger,
	Portal,
	rem,
	StyleProps,
	TrashOutline,
	useDisclosure,
} from '@bonusly/design-system';
import { useBuildRecognizeURL } from '@bonusly/url';
import { FeatureFlag } from '@bonusly/who-am-i';

import { Bonus } from '../../../../../types/bonus';
import { IconOption } from '../../components/common/dropdown/icon-option';
import { useBonusCompletionModalsStore } from '../../hooks/use-bonus-completion-modals';
import { BonusFeedTrackedAction, useTrackBonusFeedItemEvent } from '../../hooks/use-bonus-feed-item-analytics';
import { useCreateBonusReport } from '../../hooks/use-bonus-reports';
import { useContextualizedBonusDetails } from '../../hooks/use-contextualized-bonus-details';
import { useToast } from '../../hooks/use-toast';
import { InviteModal } from '../invite';
import { FormState, ReportBonusModal, ReportBonusModalActions } from './report-bonus-modal';

export interface BonusOptionsMenuProps extends StyleProps {
	bonus: Bonus;
	onClickDelete?: VoidFunction;
	onClickEdit?: VoidFunction;
	hideBookmarkButton?: boolean;
}

export const BonusOptionsMenu: FC<BonusOptionsMenuProps> = ({ bonus, onClickDelete, onClickEdit, ...rest }) => {
	const recognizeURL = useBuildRecognizeURL();
	const { t } = useTranslation(['common', 'recognition']);

	const trackCopy = useTrackBonusFeedItemEvent(bonus, BonusFeedTrackedAction.BonusUrlCopy);
	const trackReport = useTrackBonusFeedItemEvent(bonus, BonusFeedTrackedAction.BonusReport);
	const { createErrorToast, createSuccessToast } = useToast();
	const reportModalRef = useRef<ReportBonusModalActions>(null);

	const reportModal = useDisclosure();
	const inviteModal = useDisclosure();

	// Hide/report modal state lives in a global store: the optimistic hide
	// unmounts this whole card immediately, so local modal state would die
	// before the modal ever painted (see GlobalBonusCompletionModals).
	const openHideConfirm = useBonusCompletionModalsStore((state) => state.openHideConfirm);
	const openReportCompletion = useBonusCompletionModalsStore((state) => state.openReportCompletion);

	const { isEditableByUser } = useContextualizedBonusDetails(bonus);

	// Reporting is a heightened moment — completion gets a reassuring modal, not
	// a toast. On failure the prose modal stays open so the user's input survives.
	const reportBonus = useCreateBonusReport({
		onSuccess: () => {
			reportModalRef.current?.close();
			openReportCompletion();
		},
		onError: () => {
			createErrorToast(t('report_bonus.feedback.error', { ns: 'recognition' }));
		},
	});

	const handleCopyUrl = (onCloseMenu: VoidFunction) => () => {
		trackCopy();
		copy(recognizeURL('bonuses', bonus.id));
		createSuccessToast(t('bonus_card.feedback.link_copied_success', { ns: 'recognition' }));
		onCloseMenu();
	};

	const handleClickDelete = (onCloseMenu: VoidFunction) => () => {
		if (onClickDelete) {
			onClickDelete();
		}
		onCloseMenu();
	};

	const handleClickInvite = (onCloseMenu: VoidFunction) => () => {
		inviteModal.onOpen();
		onCloseMenu();
	};

	// No mutation here: hiding deserves a chance to consider first. The confirm
	// modal (hosted globally) owns the mutation and the rest of the flow.
	const handleClickHide = (onCloseMenu: VoidFunction) => () => {
		openHideConfirm(bonus.id);
		onCloseMenu();
	};

	const handleClickReport = (onCloseMenu: VoidFunction) => () => {
		reportModal.onOpen();
		onCloseMenu();
	};

	const handleConfirmReport = ({ reason }: FormState) => {
		trackReport();

		reportBonus.mutate({
			bonusReport: {
				bonusId: bonus.id,
				userInput: reason,
			},
		});
	};

	const handleClickEdit = (onCloseMenu: VoidFunction) => () => {
		if (onClickEdit) {
			onClickEdit();
		}
		onCloseMenu();
	};

	return (
		<>
			<Popover isLazy returnFocusOnClose={false} placement="bottom-end">
				{({ onClose }) => (
					<>
						<PopoverTrigger>
							<IconButton
								_hover={{ backgroundColor: 'secondary.bgAlpha' }}
								aria-label={t('bonus_card.menu_button_label', {
									ns: 'recognition',
								})}
								backgroundColor="transparent"
								borderRadius="full"
								color="text.default"
								h={rem(44)}
								icon={<DotsHorizontalOutline size="sm" />}
								w={rem(44)}
								{...rest}
							/>
						</PopoverTrigger>

						<Portal>
							<PopoverContent
								aria-label={
									t('bonus_card.menu_popover_label', {
										ns: 'recognition',
									}) ?? ''
								}
								boxShadow="xl"
								border="none"
								width="auto"
								onClick={(e) => e.stopPropagation()}
							>
								<Box
									as="ul"
									role="listbox"
									aria-label={
										t('bonus_card.menu_options_label', {
											ns: 'recognition',
										}) ?? ''
									}
									p={1}
									width={{ base: '100%', sm: rem(200) }}
								>
									<IconOption
										icon={<LinkAltOutline />}
										onClick={handleCopyUrl(onClose)}
										text={t('copy_link', {
											ns: 'common',
										})}
									/>

									<FeatureFlag flag="bonus-invite-others">
										<IconOption
											icon={<MailOpenOutline />}
											onClick={handleClickInvite(onClose)}
											text={t('invite_others', {
												ns: 'recognition',
											})}
										/>
									</FeatureFlag>

									<IconOption
										icon={<EyeOffOutline />}
										onClick={handleClickHide(onClose)}
										text={t('hide_bonus.menu_option', {
											ns: 'recognition',
										})}
									/>

									<IconOption
										icon={<FlagOutline />}
										onClick={handleClickReport(onClose)}
										text={t('report_bonus.menu_option', {
											ns: 'recognition',
										})}
									/>

									{isEditableByUser && onClickEdit && (
										<IconOption
											icon={<PencilAltOutline />}
											onClick={handleClickEdit(onClose)}
											text={t('edit', {
												ns: 'common',
											})}
										/>
									)}

									{isEditableByUser && onClickDelete && (
										<IconOption
											icon={<TrashOutline />}
											onClick={handleClickDelete(onClose)}
											text={t('delete', {
												ns: 'common',
											})}
										/>
									)}
								</Box>
							</PopoverContent>
						</Portal>
					</>
				)}
			</Popover>

			<ReportBonusModal
				isLoading={reportBonus.isLoading}
				isOpen={reportModal.isOpen}
				onClose={reportModal.onClose}
				onConfirm={handleConfirmReport}
				ref={reportModalRef}
			/>

			<InviteModal isOpen={inviteModal.isOpen} onClose={inviteModal.onClose} bonus={bonus} />
		</>
	);
};
